import { NoAccountsOutlined, NoAccountsTwoTone } from '@mui/icons-material';
import {
	alpha,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resetStore, useAppDispatch } from '../../../redux/store';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { deleteUser } from '../../../redux/slices/user';
import { User } from '../../../types/user';

interface DeleteAccountButtonProps {
	icon?: boolean;
	widthControl?: boolean;
	profile?: User;
	onComplete?: () => void;
}

const DeleteAccountButton = ({ icon, widthControl = false, profile }: DeleteAccountButtonProps) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { signOut } = useAuthenticator((context) => [context.user]);

	const [loading, setLoading] = useState(false);

	const handleDeleteAccount = async () => {
		setLoading(true);
		setOpen(false);

		await dispatch(deleteUser(profile));
		signOut();
		resetStore();
		navigate('/login');

		setLoading(false);
	};
	return (
		<>
			{icon ? (
				<Box position={'relative'}>
					{loading && (
						<CircularProgress
							color="error"
							size={28}
							sx={{
								position: 'absolute',
								top: 6,
								left: 6,
								zIndex: 1
							}}
						/>
					)}
					<IconButton disabled={loading} color="error" onClick={() => setOpen(true)}>
						<NoAccountsTwoTone />
					</IconButton>
				</Box>
			) : (
				<Button
					sx={{
						width: widthControl ? 'auto' : '100%',
						px: 3,
						py: 1,
						borderRadius: 999,
						fontWeight: 600,
						textTransform: 'none',
						color: 'error.main',
						bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
						border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
						boxShadow: 'none',
						'&:hover': {
							color: 'error.contrastText',
							bgcolor: 'error.main',
							border: (theme) => `1px solid ${theme.palette.error.main}`,
							boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.error.main, 0.4)}`,
							transform: 'translateY(-2px)'
						}
					}}
					variant={'contained'}
					color="error"
					startIcon={<NoAccountsOutlined />}
					onClick={() => setOpen(true)}
				>
					{t('common.actions.deleteAccount')}
				</Button>
			)}
			<Dialog open={open} onClose={() => setOpen(false)}>
				<DialogTitle>{t('common.actions.deleteAccount')}</DialogTitle>
				<DialogContent>{t('common.messages.deleteAccountSelf')}</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => setOpen(false)}>
						{t('common.actions.cancel')}
					</Button>
					<Button
						sx={{
							textTransform: 'none',
							color: 'error.contrastText',
							bgcolor: 'error.main',
							border: 'none',
							'&:hover': { bgcolor: 'error.dark', border: 'none' }
						}}
						onClick={() => handleDeleteAccount()}
						variant={'contained'}
						color="error"
					>
						{t('common.actions.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default DeleteAccountButton;
